import { Lock } from 'lucide-react';

import type { Contact } from '@/types/contacts';
import {
  getSourceLabel,
  getChannelLabel,
  formatLeadDate,
} from '@/lib/attributionDisplay';
import { EMPTY, columnCount, type ColVis } from '@/lib/contacts';
import { StatusDropdown } from './StatusDropdown';
import { ExpandedDetail } from './ExpandedDetail';

// Exception conversion states get a labeled chip. 'sent' is the common case —
// shown as a quiet ✓ with no label — and 'none'/unknown render blank, so the
// column stays low-noise and only problems draw the eye.
// 'paused' = free-plan fire-back withheld — display copy "Not sent" (the DB
// token stays 'paused'; see PLAN_FREE_TIER_REDESIGN.md).
const CONVERSION_STATE: Record<
  string,
  { label: string; dot: string; text: string }
> = {
  paused: { label: 'Not sent', dot: 'bg-amber-500', text: 'text-amber-700' },
  not_connected: {
    label: 'Not connected',
    dot: 'bg-amber-500',
    text: 'text-amber-700',
  },
  failed: { label: 'Failed', dot: 'bg-red-500', text: 'text-red-600' },
};

// The Conversion column (quiet). Backend rollup (contact.conversion_state) says
// whether the fire-back reached the ad platforms. See REFERENCE/plans/PLAN_C8_AT_CAP_UI.md.
export function ConversionState({ state }: { state?: string }) {
  if (state === 'sent') {
    return (
      <span
        className="font-bold text-emerald-600"
        title="Conversion sent to your ad platforms"
        aria-label="Sent"
      >
        ✓
      </span>
    );
  }
  const meta = state ? CONVERSION_STATE[state] : undefined;
  if (!meta) return <span className="text-muted-foreground">{EMPTY}</span>;
  return (
    <span
      className={`inline-flex items-center gap-1.5 font-medium ${meta.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

interface ContactRowProps {
  contact: Contact;
  isFirst: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  colVis: ColVis;
}

// Cell order MUST match CONTACTS_COLUMNS (lib/contacts.ts):
//   Time · Phone · From · Path · Campaign · Status · Conversion · Client · Received on
export function ContactRow({
  contact,
  isFirst,
  isExpanded,
  onToggleExpand,
  colVis,
}: ContactRowProps) {
  const { showClient, showYourNumber } = colVis;

  // Locked (over-visible-cap) row of a Free client. The backend already
  // stripped identity fields — render what's kept (time / source / path /
  // campaign), a lock in place of the phone, no expand, no status control.
  // See PLAN_FREE_TIER_REDESIGN.md §5.
  if (contact.locked) {
    return (
      <tr
        className={`select-none ${isFirst ? '' : 'border-t-2 border-dark/20'}`}
        aria-label="Locked contact — upgrade to see it"
      >
        <td className="whitespace-nowrap px-6 py-4 align-middle text-sm text-muted-foreground">
          {formatLeadDate(contact.created_at)}
        </td>
        <td className="whitespace-nowrap px-6 py-4 align-middle">
          <span className="inline-flex items-center gap-2 font-mono text-base text-muted-foreground">
            <Lock className="h-3.5 w-3.5" aria-hidden="true" />
            •• ••• ••••
          </span>
        </td>
        <td className="whitespace-nowrap px-6 py-4 align-middle text-base font-medium text-muted-foreground">
          {getSourceLabel(contact.source, contact.medium)}
        </td>
        <td className="whitespace-nowrap px-6 py-4 align-middle text-base text-muted-foreground">
          {getChannelLabel(contact.channel, contact.endpoint)}
        </td>
        <td className="px-6 py-4 align-middle text-base text-muted-foreground">
          <div className="max-w-[180px] truncate" title={contact.campaign ?? ''}>
            {contact.campaign ?? EMPTY}
          </div>
        </td>
        <td className="px-6 py-4 align-middle text-sm font-medium text-muted-foreground">
          Locked
        </td>
        <td className="px-6 py-4 align-middle text-muted-foreground">{EMPTY}</td>
        {showClient && (
          <td className="px-6 py-4 align-middle text-base text-muted-foreground">
            <div className="max-w-[160px] truncate" title={contact.client_name ?? ''}>
              {contact.client_name ?? EMPTY}
            </div>
          </td>
        )}
        {showYourNumber && (
          <td className="px-6 py-4 align-middle text-muted-foreground">{EMPTY}</td>
        )}
      </tr>
    );
  }

  return (
    <>
      <tr
        onClick={onToggleExpand}
        className={`cursor-pointer transition-colors hover:bg-bg-tint ${isFirst ? '' : 'border-t-2 border-dark/20'}`}
      >
        <td className="whitespace-nowrap px-6 py-4 align-middle text-sm text-dark">
          {formatLeadDate(contact.created_at)}
        </td>
        <td className="whitespace-nowrap px-6 py-4 align-middle font-mono text-base text-dark">
          {contact.phone}
        </td>
        <td className="whitespace-nowrap px-6 py-4 align-middle text-base font-medium text-dark">
          {getSourceLabel(contact.source, contact.medium)}
        </td>
        <td className="whitespace-nowrap px-6 py-4 align-middle text-base text-dark">
          {getChannelLabel(contact.channel, contact.endpoint)}
        </td>
        <td className="px-6 py-4 align-middle text-base text-dark">
          {/* Campaign varies the most in width; cap + native title tooltip so
              long agency-tier values don't push later columns off the edge. */}
          <div className="max-w-[180px] truncate" title={contact.campaign ?? ''}>
            {contact.campaign ?? EMPTY}
          </div>
        </td>
        <td className="px-6 py-4 align-middle">
          <StatusDropdown contact={contact} />
        </td>
        <td className="px-6 py-4 align-middle">
          <ConversionState state={contact.conversion_state} />
        </td>
        {showClient && (
          <td className="px-6 py-4 align-middle text-base text-muted-foreground">
            <div
              className="max-w-[160px] truncate"
              title={contact.client_name ?? ''}
            >
              {contact.client_name ?? EMPTY}
            </div>
          </td>
        )}
        {showYourNumber && (
          <td className="px-6 py-4 align-middle font-mono text-sm text-muted-foreground">
            <div
              className="max-w-[140px] truncate"
              title={contact.tracking_number ?? ''}
            >
              {contact.tracking_number ?? EMPTY}
            </div>
          </td>
        )}
      </tr>

      {isExpanded && (
        <tr className="border-b-2 border-t-2 border-dark/15 bg-bg-tint">
          <td colSpan={columnCount(colVis)} className="px-6 py-6">
            <ExpandedDetail contact={contact} />
          </td>
        </tr>
      )}
    </>
  );
}
